import { useEffect, useRef } from 'react';
import type { Participant } from '@shared/types';
import { CameraIcon, UserIcon, MicOffIcon } from '@shared/ui';

interface ParticipantTileProps {
  participant: Participant;
  className?: string;
}

export function ParticipantTile({ participant, className = '' }: ParticipantTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (videoRef.current && participant.videoTrack) {
      const stream = new MediaStream([participant.videoTrack]);
      videoRef.current.srcObject = stream;
    } else if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [participant.videoTrack]);

  useEffect(() => {
    if (audioRef.current && participant.audioTrack && !participant.isLocal) {
      audioRef.current.srcObject = new MediaStream([participant.audioTrack]);
    } else if (audioRef.current) {
      audioRef.current.srcObject = null;
    }
  }, [participant.audioTrack, participant.isLocal]);

  const hasVideo = !!participant.videoTrack && participant.isCameraEnabled;

  return (
    <div
      className={`relative min-h-[180px] overflow-hidden rounded-lg border bg-surface-1 ${
        participant.isSpeaking ? 'border-accent' : 'border-surface-border'
      } ${className}`}
    >
      {/* Video */}
      <video
        ref={videoRef}
        autoPlay
        muted={participant.isLocal}
        playsInline
        className={`h-full w-full object-cover ${hasVideo ? 'opacity-100' : 'opacity-0'}`}
      />
      <audio ref={audioRef} autoPlay />

      {/* Placeholder when camera is off */}
      {!hasVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-2">
          <div className="flex flex-col items-center gap-2">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface-3">
              <UserIcon className="h-7 w-7 text-text-muted" />
            </div>
            <span className="text-xs text-text-muted">{participant.name}</span>
          </div>
        </div>
      )}

      {/* Bottom overlay: name + mic/cam status */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/70 px-3 py-2">
        <span className="truncate text-xs font-medium text-white">
          {participant.name}
          {participant.isLocal && (
            <span className="ml-1 text-xs text-text-muted">(you)</span>
          )}
        </span>
        <div className="flex items-center gap-1.5">
          {!participant.isMicEnabled && (
            <span className="rounded bg-status-live/80 p-0.5">
              <MicOffIcon className="h-3 w-3 text-white" />
            </span>
          )}
          {!participant.isCameraEnabled && (
            <span className="rounded bg-surface-3/80 p-0.5">
              <CameraIcon className="h-3 w-3 text-text-muted" />
            </span>
          )}
        </div>
      </div>

      {/* Speaking indicator */}
      {participant.isSpeaking && (
        <div className="absolute right-2 top-2 h-2 w-2 animate-pulse rounded-full bg-accent" />
      )}
    </div>
  );
}
